/* eslint-disable @tanstack/query/exhaustive-deps */
import { useQuery } from '@tanstack/react-query';
import { HTTP_METHODS } from '@/utils/constants/httpMethods';
import { FeatureConfig } from '@/utils/constants/featuresFlag';
import { buildDateQueryString } from '@/helpers/buildDateQueryString';
import { formatDateParam } from '@/helpers/formatDateParam';
import { request } from '@/helpers/apiClient';
import { buildFilterQueryString } from '@/helpers/buildFilterQueryString';
import { stabilizeFilters } from '@/helpers/stabilizeFilters';

// API Endpoints
const oktaStatusCountApiUrl = 'api/v1/adminui/onboarding/okta-status-count';
const storeCountApiUrl = 'api/v1/adminui/onboarding/store-count';
const missingVisitListCountApiUrl =
  'api/v1/adminui/onboarding/stores-missing-visit-list-info-count';
const missingSegmentsCountApiUrl =
  'api/v1/adminui/onboarding/stores-missing-segments-count';
const missingTargetsCountApiUrl =
  'api/v1/adminui/onboarding/stores-missing-targets-count';
const withoutPromoCountApiUrl =
  'api/v1/adminui/onboarding/stores-without-promo-count';
const missingActivityCountApiUrl =
  'api/v1/adminui/onboarding/stores-missing-activity-count';
const customersApiUrl = 'api/v1/adminui/customers';
const visitListDetailsApiUrl = 'api/v1/adminui/onboarding/visit-list-details';
const missingPromoApiUrl =
  'api/v1/adminui/onboarding/stores-without-promo-details';
const missingActivityApiUrl =
  'api/v1/adminui/onboarding/stores-missing-activity-details';
const missingTargetsApiUrl =
  'api/v1/adminui/onboarding/stores-missing-targets-details';
const missingSegmentsApiUrl =
  'api/v1/adminui/onboarding/stores-missing-segments-details';

// Common Headers, assuming similar requirements to customerService
const commonHeaders = {
  'x-api-key': import.meta.env[FeatureConfig.cepApiKey],
  siteId: import.meta.env[FeatureConfig.cepSiteId]
};

/**
 * API configuration for onboarding-related endpoints
 */
export const ONBOARDING_CONFIG = {
  GET_OKTA_STATUS_COUNT: (startDate = null, endDate = null) => ({
    method: HTTP_METHODS.GET,
    endPoint: `${oktaStatusCountApiUrl}${buildDateQueryString(
      startDate,
      endDate
    )}`,
    baseUrlKey: 'cepApiUrl', // Assuming same base URL key as customerService
    headers: commonHeaders
  }),
  GET_STORE_COUNT: (startDate = null, endDate = null) => ({
    method: HTTP_METHODS.GET,
    endPoint: `${storeCountApiUrl}${buildDateQueryString(startDate, endDate)}`,
    baseUrlKey: 'cepApiUrl',
    headers: commonHeaders
  }),
  GET_MISSING_VISIT_LIST_COUNT: (startDate = null, endDate = null) => ({
    method: HTTP_METHODS.GET,
    endPoint: `${missingVisitListCountApiUrl}${buildDateQueryString(
      startDate,
      endDate
    )}`,
    baseUrlKey: 'cepApiUrl',
    headers: commonHeaders
  }),
  GET_MISSING_SEGMENTS: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `${missingSegmentsApiUrl}?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }
    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: commonHeaders
    };
  },
  GET_MISSING_SEGMENTS_COUNT: (startDate = null, endDate = null) => ({
    method: HTTP_METHODS.GET,
    endPoint: `${missingSegmentsCountApiUrl}${buildDateQueryString(
      startDate,
      endDate
    )}`,
    baseUrlKey: 'cepApiUrl',
    headers: commonHeaders
  }),
  GET_MISSING_TARGETS: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null,
    targetType = null
  ) => {
    let url = `${missingTargetsApiUrl}?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }
    url += buildFilterQueryString(filters);

    // Append targetType if provided
    if (targetType) {
      url += `&targetType=${targetType}`;
    }

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: commonHeaders
    };
  },
  GET_MISSING_TARGETS_COUNT: (startDate = null, endDate = null) => ({
    method: HTTP_METHODS.GET,
    endPoint: `${missingTargetsCountApiUrl}${buildDateQueryString(
      startDate,
      endDate
    )}`,
    baseUrlKey: 'cepApiUrl',
    headers: commonHeaders
  }),
  GET_MISSING_PROMO: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `${missingPromoApiUrl}?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }
    url += buildFilterQueryString(filters);
    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: commonHeaders
    };
  },
  GET_WITHOUT_PROMO_COUNT: (startDate = null, endDate = null) => ({
    method: HTTP_METHODS.GET,
    endPoint: `${withoutPromoCountApiUrl}${buildDateQueryString(
      startDate,
      endDate
    )}`,
    baseUrlKey: 'cepApiUrl',
    headers: commonHeaders
  }),
  GET_MISSING_ACTIVITY: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `${missingActivityApiUrl}?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }
    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: commonHeaders
    };
  },
  GET_MISSING_ACTIVITY_COUNT: (startDate = null, endDate = null) => ({
    method: HTTP_METHODS.GET,
    endPoint: `${missingActivityCountApiUrl}${buildDateQueryString(
      startDate,
      endDate
    )}`,
    baseUrlKey: 'cepApiUrl',
    headers: commonHeaders
  }),
  GET_CUSTOMER_LIST_BY_OKTA_STATUS: (
    page = 0,
    pageSize = 10,
    filters = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `${customersApiUrl}?page=${page}&pageSize=${pageSize}`;
    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }
    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: commonHeaders
    };
  },
  GET_VISIT_LIST_DETAILS: (
    page = 0,
    pageSize = 10,
    filters = {},
    // searchParams = {},
    startDate = null,
    endDate = null
  ) => {
    let url = `${visitListDetailsApiUrl}?page=${page}&pageSize=${pageSize}`;

    const dateQS = buildDateQueryString(startDate, endDate);
    if (dateQS) {
      url += dateQS.replace('?', '&');
    }

    url += buildFilterQueryString(filters);

    return {
      method: HTTP_METHODS.GET,
      endPoint: url,
      baseUrlKey: 'cepApiUrl',
      headers: commonHeaders
    };
  }
};

// --- Fetch Functions ---

/** Fetches Okta status counts based on optional date range */
export const fetchOktaStatusCount = async (
  startDate = null,
  endDate = null
) => {
  const response = await request(
    ONBOARDING_CONFIG.GET_OKTA_STATUS_COUNT(startDate, endDate)
  );
  return response?.oktaStatusData;
};

/** Fetches total store count based on optional date range */
export const fetchStoreCount = async (startDate = null, endDate = null) => {
  const response = await request(
    ONBOARDING_CONFIG.GET_STORE_COUNT(startDate, endDate)
  );
  return response?.storeCount;
};

/** Fetches count of stores missing visit list info based on optional date range */
export const fetchMissingVisitListCount = async (
  startDate = null,
  endDate = null
) => {
  const response = await request(
    ONBOARDING_CONFIG.GET_MISSING_VISIT_LIST_COUNT(startDate, endDate)
  );
  return response?.visitListInfoCount;
};

/** Fetches stores missing Segments based on optional date range */
export const fetchMissingSegments = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return request(
    ONBOARDING_CONFIG.GET_MISSING_SEGMENTS(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

/** Fetches count of stores missing segments based on optional date range */
export const fetchMissingSegmentsCount = async (
  startDate = null,
  endDate = null
) => {
  const response = await request(
    ONBOARDING_CONFIG.GET_MISSING_SEGMENTS_COUNT(startDate, endDate)
  );
  return response?.segmentCount;
};

/** Fetches stores missing targets based on optional date range */
export const fetchMissingTargets = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null,
  targetType = null
) => {
  const response = await request(
    ONBOARDING_CONFIG.GET_MISSING_TARGETS(
      page,
      pageSize,
      filters,
      startDate,
      endDate,
      targetType
    )
  );
  
  if (!response) {
    throw new Error('No response received from server');
  }

  // Check if response is an empty object
  if (Object.keys(response).length === 0) {
    throw new Error('Empty response received from server');
  }
  
  return response;
};

/** Fetches count of stores missing targets based on optional date range */
export const fetchMissingTargetsCount = async (
  startDate = null,
  endDate = null
) => {
  const response = await request(
    ONBOARDING_CONFIG.GET_MISSING_TARGETS_COUNT(startDate, endDate)
  );
  return response?.storesMissingTargetsCount;
};

/** Fetches stores missing promo based on optional date range */
export const fetchMissingPromo = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return request(
    ONBOARDING_CONFIG.GET_MISSING_PROMO(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

/** Fetches count of stores without promotions based on optional date range */
export const fetchWithoutPromoCount = async (
  startDate = null,
  endDate = null
) => {
  const response = await request(
    ONBOARDING_CONFIG.GET_WITHOUT_PROMO_COUNT(startDate, endDate)
  );
  return response?.storesWithoutPromoCount;
};

/** Fetches stores missing activity based on optional date range */
export const fetchMissingActivity = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return await request(
    ONBOARDING_CONFIG.GET_MISSING_ACTIVITY(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

/** Fetches count of stores missing activities based on optional date range */
export const fetchMissingActivityCount = async (
  startDate = null,
  endDate = null
) => {
  const response = await request(
    ONBOARDING_CONFIG.GET_MISSING_ACTIVITY_COUNT(startDate, endDate)
  );
  return response?.storesMissingActivityCount;
};

/**
 * Fetches paginated customer list based on filters (including Okta status).
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters (e.g., { oktaStatus: 'Active', name: '...' })
 * @param {Date | null} startDate - Optional start date filter
 * @param {Date | null} endDate - Optional end date filter
 * @returns {Promise} - Promise with customer list data
 */
export const fetchCustomerListByOktaStatus = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return request(
    ONBOARDING_CONFIG.GET_CUSTOMER_LIST_BY_OKTA_STATUS(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

/**
 * Fetches detailed visit list information with pagination, sorting, filtering, and search.
 * @param {number} page - Page number
 * @param {number} pageSize - Items per page
 * @param {Object} filters - Boolean filters (e.g., { filterMissingMoa: true })
 * @param {Object} searchParams - Search criteria (e.g., { searchErpId: '123' })
 * @param {Date | null} startDate - Optional start date filter
 * @param {Date | null} endDate - Optional end date filter
 * @returns {Promise} - Promise with visit list details data
 */
export const fetchVisitListDetails = async (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  return request(
    ONBOARDING_CONFIG.GET_VISIT_LIST_DETAILS(
      page,
      pageSize,
      filters,
      startDate,
      endDate
    )
  );
};

// --- React Query Hooks ---

/**
 * Generic factory for creating onboarding count query hooks.
 * @param {string} queryKeyPrefix - Unique prefix for the query key.
 * @param {Function} fetchFn - The async function to fetch data.
 * @returns {Function} - React Query hook.
 */
const createOnboardingCountHook =
  (queryKeyPrefix, fetchFn) =>
  (startDate = null, endDate = null, options = {}) => {
    // Use formatted dates in the query key to ensure stable keys when Date objects change slightly (e.g., time part)
    const formattedStartDate = formatDateParam(startDate);
    const formattedEndDate = formatDateParam(endDate);

    return useQuery({
      // Query key includes prefix and formatted dates for caching based on selection
      queryKey: [queryKeyPrefix, formattedStartDate, formattedEndDate],
      queryFn: () => fetchFn(startDate, endDate),
      // Only run the query if both dates are available, unless explicitly enabled
      enabled:
        options.enabled !== undefined
          ? options.enabled
          : !!(startDate && endDate),
      // keepPreviousData: true,
      // retry: 1,
      // retryDelay: 0,
      ...options
    });
  };

// Export specific hooks for each data point
export const useOktaStatusCount = createOnboardingCountHook(
  'oktaStatusCount',
  fetchOktaStatusCount
);
export const useStoreCount = createOnboardingCountHook(
  'storeCount',
  fetchStoreCount
);
export const useMissingVisitListCount = createOnboardingCountHook(
  'missingVisitListCount',
  fetchMissingVisitListCount
);
export const useMissingSegmentsCount = createOnboardingCountHook(
  'missingSegmentsCount',
  fetchMissingSegmentsCount
);
export const useMissingTargetsCount = createOnboardingCountHook(
  'missingTargetsCount',
  fetchMissingTargetsCount
);
export const useWithoutPromoCount = createOnboardingCountHook(
  'withoutPromoCount',
  fetchWithoutPromoCount
);
export const useMissingActivityCount = createOnboardingCountHook(
  'missingActivityCount',
  fetchMissingActivityCount
);

/**
 * React Query hook for retrieving and caching paginated customer list by Okta status.
 * @param {number} page - Page number for pagination
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters (including oktaStatus)
 * @param {Date | null} startDate - Optional start date filter
 * @param {Date | null} endDate - Optional end date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useCustomerListByOktaStatus = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);
  const stableFilters = stabilizeFilters(filters);
  return useQuery({
    queryKey: [
      'customerListByOktaStatus',
      page,
      pageSize,
      JSON.stringify(stableFilters),
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchCustomerListByOktaStatus(
        page,
        pageSize,
        stableFilters,
        startDate,
        endDate
      )
    // keepPreviousData: true,
    // retry: 3,
    // retryDelay: 0
  });
};

/**
 * React Query hook for retrieving and caching detailed visit list information.
 * @param {number} page - Page number
 * @param {number} pageSize - Items per page
 * @param {Object} filters - Boolean filters
 * @param {Object} searchParams - Search criteria
 * @param {Date | null} startDate - Optional start date filter
 * @param {Date | null} endDate - Optional end date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useVisitListDetails = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);
  const stableFilters = stabilizeFilters(filters);

  return useQuery({
    queryKey: [
      'visitListDetails',
      page,
      pageSize,
      JSON.stringify(stableFilters),
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchVisitListDetails(page, pageSize, stableFilters, startDate, endDate)
  });
};

/**
 * React Query hook for retrieving and caching paginated missing promo data.
 * @param {number} page - Page number
 * @param {number} pageSize - Number of items per page
 * @param {Object} filters - Filter parameters
 * @param {Date | null} startDate - Optional start date filter
 * @param {Date | null} endDate - Optional end date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useMissingPromo = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);
  const stableFilters = stabilizeFilters(filters);
  return useQuery({
    queryKey: [
      'missingPromo',
      page,
      pageSize,
      JSON.stringify(stableFilters),
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchMissingPromo(page, pageSize, stableFilters, startDate, endDate)
  });
};

/**
 * React Query hook for retrieving and caching paginated missing activity data.
 * @param {number} page - Page number
 * @param {number} pageSize - Items per page
 * @param {Object} filters - Filter parameters
 * @param {Date | null} startDate - Optional start date filter
 * @param {Date | null} endDate - Optional end date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useMissingActivity = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);
  const stableFilters = stabilizeFilters(filters);

  return useQuery({
    queryKey: [
      'missingActivity',
      page,
      pageSize,
      JSON.stringify(stableFilters),
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchMissingActivity(page, pageSize, stableFilters, startDate, endDate)
  });
};

/**
 * React Query hook for retrieving and caching paginated missing segments data.
 * @param {number} page - Page number
 * @param {number} pageSize - Items per page
 * @param {Object} filters - Filter parameters
 * @param {Date | null} startDate - Optional start date filter
 * @param {Date | null} endDate - Optional end date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useMissingSegments = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);
  const stableFilters = stabilizeFilters(filters);

  return useQuery({
    queryKey: [
      'missingSegments',
      page,
      pageSize,
      JSON.stringify(stableFilters),
      formattedStartDate,
      formattedEndDate
    ],
    queryFn: () =>
      fetchMissingSegments(page, pageSize, stableFilters, startDate, endDate)
  });
};

/**
 * React Query hook for retrieving and caching paginated missing targets data.
 * @param {number} page - Page number
 * @param {number} pageSize - Items per page
 * @param {Object} filters - Filter parameters
 * @param {Date | null} startDate - Optional start date filter
 * @param {Date | null} endDate - Optional end date filter
 * @returns {UseQueryResult} - React Query result object
 */
export const useMissingTargets = (
  page = 0,
  pageSize = 10,
  filters = {},
  startDate = null,
  endDate = null,
  targetType = null,
  options = {}
) => {
  const formattedStartDate = formatDateParam(startDate);
  const formattedEndDate = formatDateParam(endDate);
  const stableFilters = stabilizeFilters(filters);
  return useQuery({
    queryKey: [
      'missingTargets',
      page,
      pageSize,
      JSON.stringify(stableFilters),
      formattedStartDate,
      formattedEndDate,
      targetType
    ],
    queryFn: () =>
      fetchMissingTargets(
        page,
        pageSize,
        stableFilters,
        startDate,
        endDate,
        targetType
      ),
      ...options
  });
};
